import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(t('account.too_short'));
      return;
    }
    if (next !== confirm) {
      toast.error(t('account.mismatch'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success(t('account.updated'));
      resetForm();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'WRONG_PASSWORD') {
        toast.error(t('account.wrong_password'));
      } else if (detail === 'PASSWORD_TOO_SHORT') {
        toast.error(t('account.too_short'));
      } else {
        toast.error(t('account.error'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) resetForm(); } }}>
      <DialogContent className="max-w-sm bg-[hsl(210,18%,9%)] border-border" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <KeyRound size={16} className="text-cyan-400" />
            {t('account.change_password')}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('account.google_hint')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t('account.current_password')}</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t('account.new_password')}</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              autoComplete="new-password"
              className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t('account.confirm_password')}</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              autoComplete="new-password"
              className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
              data-testid="confirm-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-10 gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)] font-semibold"
            data-testid="change-password-submit"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            {saving ? t('account.saving') : t('account.save')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
